import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { UserTypeEnum } from '@cai-services';
import { Subscription } from 'rxjs';
import { UserFeaturesEnum } from '../../core/_enums/user-features.enum';
import { Office } from '../../core/_models/office.model';
import { SessionService } from '../../core/_services/session.service';
import { getDefaultOffice } from '../../helpers/office-helpers';
import { HomepageCommonUtil } from '../../utils/homepage.util';

import { ApplicationProperties } from '../../core/_base/layout/models/app-properties.model';
import { APP_PROPS } from '../../cai-common.module';

@Component({
  selector: 'kt-office-selector',

  templateUrl: './office-selector.component.html',
  styleUrls: ['./office-selector.component.scss'],
  standalone: false,
})
export class OfficeSelectorComponent implements OnInit, OnDestroy {
  @Input() type: string;
  @Input() appearance: 'icon' | 'expanded';
  @Input() showIataCass: boolean;
  @Input() showIcon: boolean;
  @Output() onSelect = new EventEmitter();

  companyOffices: Office[] = [];
  companyOfficesView: OfficeView[];
  selectedOfficeView: OfficeView;

  private officeChangedStatusSub: Subscription;

  private proPageRequirements = [
    {
      page: '/forwarder/quote/shipper-quote',
      activatedFeatures: [UserFeaturesEnum.QUOTATION],
    },
    {
      page: '/forwarder/compare-rates',
      activatedFeatures: [
        UserFeaturesEnum.RATE_MANAGEMENT_ACCESS,
        UserFeaturesEnum.RATE_MANAGEMENT_UPLOAD,
      ],
    },
    {
      page: '/forwarder/rate-sheet',
      activatedFeatures: [
        UserFeaturesEnum.RATE_MANAGEMENT_ACCESS,
        UserFeaturesEnum.RATE_MANAGEMENT_UPLOAD,
      ],
    },
    {
      page: '/forwarder/dashboard/market-analysis',
      activatedFeatures: [UserFeaturesEnum.ANALYTICS_FORWARDER],
    },
    {
      page: '/forwarder/dashboard/airline-contacts',
      activatedFeatures: [UserFeaturesEnum.ANALYTICS_AIRLINE_CONTACT],
    },
    {
      page: '/forwarder/dashboard/quotation',
      activatedFeatures: [UserFeaturesEnum.QUOTATION],
    },
  ];

  constructor(
    @Inject(APP_PROPS)
    private readonly appProperties: ApplicationProperties,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly sessionService: SessionService,
  ) {
    this.officeChangedStatusSub =
      this.sessionService.officeChangedStatusObs.subscribe(async (_) => {
        if (!this.companyOffices?.length) {
          await this.initCompanyOfficesView();
        }
        this.initSelectedOfficeView();
      });
  }

  async ngOnInit() {
    this.initSelectedOfficeView();
  }

  ngOnDestroy(): void {
    this.companyOffices = null;
    if (this.officeChangedStatusSub) {
      this.officeChangedStatusSub.unsubscribe();
    }
  }

  async initCompanyOfficesView() {
    const user = this.sessionService.getCurrentUser();
    if (!user) {
      return;
    }
    this.companyOffices = (await this.sessionService.getOffices()) as Office[];
    const defaultOffice = getDefaultOffice(this.companyOffices);

    this.companyOfficesView = this.companyOffices.map(this.getView);
    const defaultOfficeView = this.companyOfficesView.find(
      (office) => office.id === defaultOffice.id,
    );
    if (defaultOfficeView !== undefined) {
      defaultOfficeView.isDefault = true;
    }
    this.companyOfficesView.sort((officeViewA, officeViewB) => {
      if (officeViewA.isDefault) {
        return -1;
      }
      if (officeViewB.isDefault) {
        return 1;
      }
      return officeViewA.airportCode?.localeCompare(officeViewB.airportCode);
    });
  }

  initSelectedOfficeView() {
    if (!this.companyOffices?.length) {
      return;
    }

    const selectedOffice = this.sessionService.getSelectedOffice(),
      companySelectedOffice = this.companyOffices.find(
        (office) => office.id === selectedOffice?.id,
      );

    if (selectedOffice && companySelectedOffice) {
      this.selectedOfficeView = this.getView(selectedOffice);
    } else {
      this.onSelectOffice(getDefaultOffice(this.companyOffices).id.toString());
    }
    this.cdr.detectChanges();
  }

  getView(office: Office): OfficeView {
    return {
      id: office.id,
      officeName: office.name,
      cityName: office.addrCity,
      countryName: office.country.countryName,
      countryCode: office.country.countryCode,
      iata: office.iata,
      cass: office.cass,
      value: office.id.toString(),
      airportCode: office?.airports?.[0]?.airportCode,
      isDefault: false,
    };
  }
  get isCargoWallet(): boolean {
    return this.appProperties.name === 'cargowallet';
  }
  onSelectOffice(officeId: string) {
    const selectedOffice = this.companyOffices.find(
      (office) => office.id === +officeId,
    );
    this.sessionService.setSelectedOffice(selectedOffice);
    if (this.sessionService.getCurrentUser().type === UserTypeEnum.FORWARDER) {
      this.checkHasAccessToPage();
    }
  }

  checkHasAccessToPage() {
    const proPageRequirement = this.proPageRequirements.find(
      (propage) => propage.page === this.router.url,
    );
    if (proPageRequirement) {
      const haveAccess = proPageRequirement.activatedFeatures.every((feature) =>
        this.hasAccessToFeature(feature),
      );
      if (!haveAccess) {
        this.navigateToSearchPage();
      }
    }
  }

  hasAccessToFeature(feature) {
    const officeId = this.sessionService.getSelectedOfficeId(),
      officeAccess = this.sessionService
        .getCurrentUser()
        ?.officeAccesses?.find((oa) => oa.office.id == officeId),
      office = officeAccess ? officeAccess.office : null;
    return HomepageCommonUtil.isFeatureEnabled(
      office?.activatedFeatures,
      feature,
    );
  }

  navigateToSearchPage() {
    this.router.navigateByUrl('forwarder/search/forwarder-search');
  }

  getValue(text: string): string {
    if (!!text && ![null, undefined, 'null', 'undefined'].includes(text)) {
      return text;
    }
    return '';
  }
}

interface OfficeView {
  id: number;
  officeName: string;
  cityName: string;
  countryName: string;
  countryCode: string;
  iata: string;
  cass: string;
  value: string;
  airportCode: string;
  isDefault: boolean;
}
